import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { ApiError } from '@/api/client';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useT } from '@/i18n/LanguageContext';

/**
 * COMMERCIAL LAYER epic (ADR-075 Part C). Two steps of the SAME domain
 * flow the previous agent built and tested end-to-end without an email
 * provider (`requestPasswordResetForKnownUser`/`confirmPasswordReset`):
 *
 *  1. Request — POST /api/v1/portal/auth/password-reset/request. Always
 *     returns a generic 200 regardless of whether the email exists (the
 *     backend never puts the raw token in this response - ADR-075 §Part
 *     B.3, an explicit, deliberate choice, not an oversight this screen
 *     should route around).
 *  2. Confirm — POST /api/v1/portal/auth/password-reset/confirm, reached
 *     via `/reset-password?token=...` OR `/invite?token=...` (SAME token
 *     mechanism, SAME confirm endpoint - sellerAuth.ts's
 *     confirmPasswordReset activates a PENDING user exactly as it resets
 *     an ACTIVE one's password; only the emailed copy and this screen's
 *     wording differ, via the `variant` prop below). Wiring a real
 *     transactional email provider to DELIVER these links is the epic's
 *     one remaining, reported STOP (NEXT.md) - until then, a token is only
 *     ever obtainable by reading `seller_password_reset_token` directly, or
 *     (invite/resend only) from the Ops UI's one-time setupUrl.
 */
export function RequestResetPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [limited, setLimited] = useState<unknown>(null);
  const t = useT();

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setLimited(null);
    try {
      await endpoints.requestPasswordReset(email.trim());
      setDone(true); // always shown, whether or not the email exists - see docstring above
    } catch (err) {
      // RATE-01: a throttled request sent nothing - say so instead of the
      // generic "check your inbox". Any other failure keeps the generic answer.
      if (err instanceof ApiError && err.status === 429) setLimited(err);
      else setDone(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-shell card">
      <h1 style={{ fontSize: 18, marginTop: 0 }}>{t('resetPassword.title')}</h1>
      {done ? (
        <p>{t('resetPassword.requestDone')}</p>
      ) : (
        <form onSubmit={onSubmit}>
          {limited !== null && <ErrorBanner error={limited} />}
          <p className="sub" style={{ marginTop: -8 }}>
            {t('resetPassword.requestHint')}
          </p>
          <div className="field-row">
            <label htmlFor="reset-email">{t('login.email')}</label>
            <input id="reset-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="form-actions">
            <button type="submit" className="primary" disabled={submitting || !email.trim()}>
              {submitting ? t('resetPassword.sending') : t('resetPassword.sendResetLink')}
            </button>
          </div>
        </form>
      )}
      <p className="sub" style={{ marginTop: 16 }}>
        <Link to="/login">{t('resetPassword.backToSignIn')}</Link>
      </p>
    </div>
  );
}

type SetPasswordVariant = 'reset' | 'invite';
type TokenCheck =
  | { status: 'checking' }
  | { status: 'valid' }
  | { status: 'invalid'; reason: 'INVALID' | 'USED' | 'EXPIRED' }
  | { status: 'error'; error: unknown };

/**
 * Shared by `/reset-password` (variant="reset") and `/invite`
 * (variant="invite") - same form, same confirm call, only the copy differs
 * by variant. Validates the token via a read-only GET on mount so a stale
 * link shows "expired/already used" immediately, never the plain set-
 * password form and never a silent fall-through to /login (the bug this
 * screen exists to fix - see App.tsx for why /invite previously had no
 * route at all).
 */
export function SetPasswordPage({ variant }: { variant: SetPasswordVariant }): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [check, setCheck] = useState<TokenCheck>({ status: 'checking' });
  const [attempt, setAttempt] = useState(0);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);
  const t = useT();

  useEffect(() => {
    if (!token) {
      setCheck({ status: 'invalid', reason: 'INVALID' });
      return;
    }
    let cancelled = false;
    setCheck({ status: 'checking' });
    endpoints.validatePasswordResetToken(token).then(
      (r) => {
        if (cancelled) return;
        setCheck(r.valid ? { status: 'valid' } : { status: 'invalid', reason: r.reason });
      },
      (error: unknown) => {
        // RATE-01: e.g. 429 - show why and let the seller retry, instead of an endless spinner.
        if (!cancelled) setCheck({ status: 'error', error });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [token, attempt]);

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (mismatch) return;
    setSubmitting(true);
    setError(null);
    try {
      await endpoints.confirmPasswordReset(token, newPassword);
      setDone(true);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  if (check.status === 'error') {
    return (
      <div className="login-shell card">
        <ErrorBanner error={check.error} onRetry={() => setAttempt((n) => n + 1)} />
      </div>
    );
  }

  if (check.status === 'checking') {
    return (
      <div className="login-shell card">
        <LoadingState label={t('resetPassword.checkingLink')} />
      </div>
    );
  }

  // Covers both "no token in the URL at all" and "the server says this
  // token is invalid/used/expired" - one unambiguous message, checked
  // BEFORE the form ever renders, so re-opening a spent link never shows a
  // plain form (or falls through to a login screen - the reported bug).
  if (check.status === 'invalid') {
    const reasonKey =
      check.reason === 'USED'
        ? 'resetPassword.tokenUsed'
        : check.reason === 'EXPIRED'
          ? 'resetPassword.tokenExpired'
          : 'resetPassword.tokenInvalid';
    return (
      <div className="login-shell card">
        <h1 style={{ fontSize: 18, marginTop: 0 }}>{t('resetPassword.linkExpiredOrUsed')}</h1>
        <p>{t(reasonKey)}</p>
        <p className="sub">
          <Link to="/forgot-password">{t('resetPassword.requestNewLink')}</Link>
        </p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="login-shell card">
        <h1 style={{ fontSize: 18, marginTop: 0 }}>
          {t(variant === 'invite' ? 'resetPassword.accountActivated' : 'resetPassword.passwordUpdated')}
        </h1>
        <p>{t(variant === 'invite' ? 'resetPassword.accountActivatedHint' : 'resetPassword.passwordUpdatedHint')}</p>
        <p className="sub">
          <Link to="/login">{t('login.signIn')}</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="login-shell card">
      <h1 style={{ fontSize: 18, marginTop: 0 }}>
        {t(variant === 'invite' ? 'resetPassword.setUpAccount' : 'resetPassword.chooseNewPassword')}
      </h1>
      <form onSubmit={onSubmit}>
        <div className="field-row">
          <label htmlFor="new-password">{t('resetPassword.newPassword')}</label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
        </div>
        <div className="field-row">
          <label htmlFor="confirm-password">{t('resetPassword.confirmPassword')}</label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>
        {mismatch && (
          <div className="error-banner" role="alert">
            {t('resetPassword.passwordMismatch')}
          </div>
        )}
        {Boolean(error) && (
          <div className="error-banner" role="alert">
            {describeResetError(t, error)}
          </div>
        )}
        <div className="form-actions">
          <button type="submit" className="primary" disabled={submitting || newPassword.length < 8 || mismatch || confirmPassword.length === 0}>
            {submitting ? t('common.saving') : t(variant === 'invite' ? 'resetPassword.setPassword' : 'resetPassword.setNewPassword')}
          </button>
        </div>
      </form>
    </div>
  );
}

function describeResetError(t: ReturnType<typeof useT>, error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'PORTAL_AUTH.RESET_TOKEN_EXPIRED') return t('resetPassword.tokenExpired');
    if (error.code === 'PORTAL_AUTH.RESET_TOKEN_USED') return t('resetPassword.tokenUsed');
    if (error.code === 'PORTAL_AUTH.RESET_TOKEN_INVALID') return t('resetPassword.tokenInvalid');
  }
  return t('resetPassword.couldNotReset');
}
